/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminSupportAccessService } from '@/services/superadmin/support-access.service';
import type { RevokeSupportAccessRequest } from '@/types/support-access';

const KEYS = {
  grants: (status: string | undefined, page: number, size: number) =>
    ['superadmin-support-access', 'grants', status, page, size] as const,
  grant: (id: string) => ['superadmin-support-access', 'grant', id] as const,
  stats: ['superadmin-support-access', 'stats'] as const,
};

export function useSuperAdminSupportGrants(status: string | undefined, page: number, size: number) {
  return useQuery({
    queryKey: KEYS.grants(status, page, size),
    queryFn: () => superAdminSupportAccessService.getGrants({ status, page, size }),
  });
}

export function useSuperAdminSupportGrant(id: string) {
  return useQuery({
    queryKey: KEYS.grant(id),
    queryFn: () => superAdminSupportAccessService.getGrant(id),
    enabled: !!id,
  });
}

export function useSuperAdminSupportStats() {
  return useQuery({
    queryKey: KEYS.stats,
    queryFn: () => superAdminSupportAccessService.getStats(),
  });
}

export function useEnterSupportSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => superAdminSupportAccessService.enterSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-support-access'] });
    },
  });
}

export function useExitSupportSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => superAdminSupportAccessService.exitSession(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-support-access'] });
    },
  });
}

export function useRevokeSupportGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request?: RevokeSupportAccessRequest }) =>
      superAdminSupportAccessService.revokeGrant(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-support-access'] });
    },
  });
}
